import { useCallback, useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { download } from '../../lib/download';
import { dtfmt, todayIso } from '../../lib/format';
import { api } from '../../lib/http';
import { Modal, useLoad } from '../ui';

/** Protokoll (Aktivitätsprotokoll): wer hat wann was im Tool gemacht. Nur für Admins mit Häkchen „Protokoll“. */

interface Row {
  id: number; at: string; requestId: string; user: { id: string | null; email: string | null; name: string | null }; via: string; viaLabel: string;
  method: string; path: string; status: number; durationMs: number; area: { id: string; name: string } | null;
  action: string; actionLabel: string; entity: { type: string; typeLabel: string; id: string | null; label: string | null } | null;
  text: string; details: Record<string, unknown>; changes: number;
}
interface Change { id: number; at: string; table: string; tableLabel: string; recordId: string; action: string; actionLabel: string; source: string; fields: { field: string; from: unknown; to: unknown }[] }
interface Detail extends Omit<Row, 'changes'> { changes: Change[] }
interface Meta { recent: { errors: number; conflicts: number; denied: number; invalid: number; total: number }; users: { email: string; name: string }[]; actions: { key: string; label: string }[]; entities: { key: string; label: string }[]; vias: { key: string; label: string }[]; areas: { id: string; name: string }[] }

type Mode = 'changes' | 'errors' | 'all';
interface Filter { mode: Mode; from: string; to: string; user: string; action: string; via: string; entity: string; area: string; status: string; q: string }
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); };
const EMPTY: Filter = { mode: 'changes', from: weekAgo(), to: todayIso(), user: '', action: '', via: '', entity: '', area: '', status: '', q: '' };

const qs = (f: Filter) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  return p.toString();
};
const TONE: Record<string, string> = { create: 'pos', update: 'info', delete: 'neg', restore: 'pos', import: 'info', export: 'info', sync: 'info', denied: 'crit', conflict: 'crit', invalid: 'neu', error: 'neg', login: 'pos', logout: 'neu', view: 'neu', other: 'neu' };
const CHANGES = ['create', 'update', 'delete', 'restore', 'import'];
const ERRORS = ['error', 'conflict', 'denied', 'invalid'];
const reason = (d: Record<string, unknown>) => [d.error, d.message ?? d.errorMessage].filter(Boolean).map(String).join(' · ');
const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? '–' : typeof v === 'object' ? JSON.stringify(v) : String(v));

export function ActivityAdmin() {
  const { t } = useI18n();
  const { data: meta } = useLoad<Meta>('/api/activity/meta');
  const [f, setF] = useState<Filter>(EMPTY);
  const [applied, setApplied] = useState<Filter>(EMPTY);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  const set = (k: keyof Filter, v: string) => setF((x) => ({ ...x, [k]: v }));

  const load = useCallback(async (filter: Filter, before?: number) => {
    setBusy(true); setError(null);
    try {
      const r = await api<{ rows: Row[]; more: boolean }>('GET', `/api/activity?${qs(filter)}${before ? `&before=${before}` : ''}&limit=100`);
      setRows((cur) => (before && cur ? [...cur, ...r.rows] : r.rows));
      setMore(r.more);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { void load(applied); }, [applied, load]);

  const apply = (e?: React.FormEvent) => { e?.preventDefault(); setApplied(f); };
  const reset = () => { setF(EMPTY); setApplied(EMPTY); };
  const setMode = (mode: Mode) => { const n = { ...f, mode, action: '', status: '', q: '' }; setF(n); setApplied(n); };
  const filtered = qs({ ...f, mode: 'changes' }) !== qs(EMPTY);
  const mode = applied.mode;
  const rc = meta?.recent;

  return (
    <>
      <p className="muted lead">{t(mode === 'errors' ? 'log.leadErrors' : mode === 'all' ? 'log.leadAll' : 'log.leadChanges')}</p>
      <div className="row" style={{ marginBottom: 12, gap: 12 }}>
        <div className="seg" role="group" aria-label={t('log.view')}>
          <button type="button" aria-pressed={mode === 'changes'} onClick={() => setMode('changes')}>{t('log.viewChanges')}</button>
          <button type="button" aria-pressed={mode === 'errors'} onClick={() => setMode('errors')}>{t('log.viewErrors')}{rc && rc.total > 0 && <span className="badge inline" aria-hidden="true">{rc.total}</span>}</button>
          <button type="button" aria-pressed={mode === 'all'} onClick={() => setMode('all')}>{t('log.viewAll')}</button>
        </div>
        {rc && (
          <span className={`pill ${rc.errors ? 'neg' : rc.total ? 'crit' : 'pos'}`} title={t('log.recentHelp')}>
            {rc.total ? t('log.recent', { n: rc.total, e: rc.errors, c: rc.conflicts, d: rc.denied, i: rc.invalid }) : t('log.recentNone')}
          </span>
        )}
      </div>
      <form className="fbar" onSubmit={apply}>
        <div className="fld"><label htmlFor="lg-from">{t('log.from')}</label><input id="lg-from" type="date" value={f.from} onChange={(e) => set('from', e.target.value)} /></div>
        <div className="fld"><label htmlFor="lg-to">{t('log.to')}</label><input id="lg-to" type="date" value={f.to} onChange={(e) => set('to', e.target.value)} /></div>
        <div className="fld"><label htmlFor="lg-u">{t('log.person')}</label><select id="lg-u" value={f.user} onChange={(e) => set('user', e.target.value)}><option value="">{t('common.all')}</option>{(meta?.users ?? []).map((u) => <option key={u.email} value={u.email}>{u.name}</option>)}</select></div>
        <div className="fld"><label htmlFor="lg-a">{t('log.action')}</label><select id="lg-a" value={f.action} onChange={(e) => set('action', e.target.value)}><option value="">{t('common.all')}</option>{(meta?.actions ?? []).filter((a) => (f.mode === 'changes' ? CHANGES.includes(a.key) : f.mode === 'errors' ? ERRORS.includes(a.key) : true)).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}</select></div>
        <div className="fld"><label htmlFor="lg-e">{t('log.entity')}</label><select id="lg-e" value={f.entity} onChange={(e) => set('entity', e.target.value)}><option value="">{t('common.all')}</option>{(meta?.entities ?? []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}</select></div>
        <div className="fld"><label htmlFor="lg-ar">{t('log.area')}</label><select id="lg-ar" value={f.area} onChange={(e) => set('area', e.target.value)}><option value="">{t('common.all')}</option>{(meta?.areas ?? []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></div>
        <div className="fld"><label htmlFor="lg-v">{t('log.via')}</label><select id="lg-v" value={f.via} onChange={(e) => set('via', e.target.value)}><option value="">{t('common.all')}</option>{(meta?.vias ?? []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}</select></div>
        {f.mode === 'all' && <div className="fld"><label htmlFor="lg-s">{t('log.result')}</label><select id="lg-s" value={f.status} onChange={(e) => set('status', e.target.value)}><option value="">{t('common.all')}</option><option value="ok">{t('log.ok')}</option><option value="failed">{t('log.failed')}</option></select></div>}
        <div className="fld"><label htmlFor="lg-q">{t('common.search')}</label><input id="lg-q" type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('log.searchPh')} /></div>
        <button className="btn primary" type="submit" disabled={busy}>{t('log.apply')}</button>
        {filtered && <button className="btn ghost" type="button" onClick={reset}>{t('common.reset')}</button>}
        <span className="spacer" />
        <ExportLog filter={applied} />
      </form>

      {!rows ? (error ? <div className="box empty"><p className="err">{error}</p></div> : <div className="box empty">{t('app.loading')}</div>) : (
        <div className="box tablewrap">
          {!rows.length ? <div className="empty">{t('log.empty')}</div> : (
            <table className="logtable">
              <thead><tr><th>{t('log.col.time')}</th><th>{t('log.person')}</th><th>{t('log.action')}</th><th>{t('log.col.what')}</th><th>{t('log.col.item')}</th>{mode === 'errors' ? <th>{t('log.col.reason')}</th> : <th>{t('log.area')}</th>}<th>{t('log.col.result')}</th>{mode !== 'errors' && <th className="r">{t('log.col.changes')}</th>}</tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="click" tabIndex={0} onClick={() => setOpen(r.id)} onKeyDown={(e) => { if (e.key === 'Enter') setOpen(r.id); }}>
                    <td className="num nowrap">{dtfmt(r.at)}</td>
                    <td>{r.user.name ?? <span className="muted">{t('log.anonymous')}</span>}{r.via !== 'browser' && <div className="muted small">{r.viaLabel}</div>}</td>
                    <td><span className={`pill ${TONE[r.action] ?? 'neu'}`}>{r.actionLabel}</span></td>
                    <td>{r.text}</td>
                    <td>{r.entity ? <>{r.entity.label ?? <span className="mono">{r.entity.id}</span>}<div className="muted small">{r.entity.typeLabel}</div></> : <span className="muted">–</span>}</td>
                    {mode === 'errors' ? <td className="small">{reason(r.details) || <span className="muted">–</span>}</td> : <td>{r.area?.name ?? <span className="muted">–</span>}</td>}
                    <td>{r.status < 400 ? <span className="pill pos">{t('log.ok')}</span> : <span className="pill neg" title={reason(r.details)}>{r.status}</span>}</td>
                    {mode !== 'errors' && <td className="r num">{r.changes || <span className="muted">–</span>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error && <p className="err" style={{ padding: '0 16px' }}>{error}</p>}
          {more && <div style={{ padding: 12 }}><button className="btn" disabled={busy} onClick={() => load(applied, rows[rows.length - 1]!.id)}>{busy ? t('app.loading') : t('log.more')}</button></div>}
        </div>
      )}
      <p className="muted small" style={{ margin: 0 }}>{t(mode === 'errors' ? 'log.helpErrors' : 'log.help')}</p>
      {open !== null && <LogDetail id={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function ExportLog({ filter }: { filter: Filter }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      {err && <span className="err small">{err}</span>}
      <button className="btn" type="button" disabled={busy} onClick={async () => {
        setBusy(true); setErr(null);
        try { await download(`/api/activity/export.xlsx?${qs(filter)}`); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
      }}>{busy ? t('common.exporting') : t('log.export')}</button>
    </>
  );
}

function LogDetail({ id, onClose }: { id: number; onClose: () => void }) {
  const { t } = useI18n();
  const { data, error } = useLoad<Detail>(`/api/activity/${id}`);
  const HIDE = new Set(['planned', 'error', 'message', 'errorMessage', 'errorStack', 'errorCode']);
  const details = data ? Object.entries(data.details).filter(([k]) => !HIDE.has(k)) : [];
  return (
    <Modal title={data ? data.text : t('log.entry')} crumb={t('log.title')} onClose={onClose} width={720} footer={<button className="btn" onClick={onClose}>{t('common.close')}</button>}>
      {!data ? (error ? <p className="err">{error}</p> : <p className="muted">{t('app.loading')}</p>) : (
        <>
          <dl className="kv">
            <dt>{t('log.col.time')}</dt><dd>{dtfmt(data.at)}</dd>
            <dt>{t('log.person')}</dt><dd>{data.user.name ?? t('log.anonymous')}{data.user.email && <span className="muted"> · {data.user.email}</span>}</dd>
            <dt>{t('log.action')}</dt><dd><span className={`pill ${TONE[data.action] ?? 'neu'}`}>{data.actionLabel}</span>{data.details.planned ? <span className="muted small"> ({t('log.planned')}: {String(data.details.planned)})</span> : null}</dd>
            {data.entity && <><dt>{t('log.col.item')}</dt><dd>{data.entity.typeLabel}: {data.entity.label ?? data.entity.id}</dd></>}
            {data.area && <><dt>{t('log.area')}</dt><dd>{data.area.name}</dd></>}
            <dt>{t('log.via')}</dt><dd>{data.viaLabel}</dd>
            <dt>{t('log.col.result')}</dt><dd>{data.status < 400 ? t('log.ok') : `${t('log.failed')} (${data.status}${data.details.error ? `, ${String(data.details.error)}` : ''})`} · {data.durationMs} ms</dd>
            {data.status >= 400 && <><dt>{t('log.col.reason')}</dt><dd>{reason(data.details) || '–'}{data.details.errorCode ? <span className="muted small"> ({String(data.details.errorCode)})</span> : null}</dd></>}
            <dt>{t('log.request')}</dt><dd className="mono">{data.method} {data.path}</dd>
            {details.map(([k, v]) => <><dt key={`k${k}`}>{k}</dt><dd key={`v${k}`} className="mono small">{fmt(v)}</dd></>)}
            <dt>{t('log.requestId')}</dt><dd className="mono small">{data.requestId}</dd>
          </dl>
          {typeof data.details.errorStack === 'string' && data.details.errorStack && (
            <details style={{ margin: '0 16px 16px' }}><summary className="muted small">{t('log.stack')}</summary><pre className="tokenbox small" style={{ whiteSpace: 'pre-wrap' }}>{data.details.errorStack}</pre></details>
          )}
          {data.changes.length > 0 && (
            <div className="box tablewrap" style={{ margin: '0 16px 16px' }}>
              <table>
                <thead><tr><th>{t('log.ch.what')}</th><th>{t('log.ch.field')}</th><th>{t('log.ch.before')}</th><th>{t('log.ch.after')}</th></tr></thead>
                <tbody>
                  {data.changes.map((c) => (c.fields.length ? c.fields : [{ field: '', from: null, to: null }]).map((fl, i) => (
                    <tr key={`${c.id}-${i}`}>
                      <td>{i === 0 && <>{c.tableLabel} · {c.actionLabel}{c.source !== 'app' && <div className="muted small">{c.source}</div>}</>}</td>
                      <td className="mono small">{fl.field}</td>
                      <td className="small">{fmt(fl.from)}</td>
                      <td className="small">{fmt(fl.to)}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
